import Application from '@ember/application';
import loadInitializers from 'ember-load-initializers';
import * as Sentry from '@sentry/ember';
import '@ember/test-waiters';
import moment from 'moment-timezone';
import 'moment/locale/it';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/en-gb';
import { setConfig } from 'ember-basic-dropdown/config';

// ----------------------------
// Redirect hash URLs to history URLs (retrocompatibility)
// ----------------------------
(function redirectHashToHistory() {
  const hash = window.location.hash;
  if (!hash || hash === '#' || hash === '') return;

  // Base URL del progetto (es: "/account/", "/erp/", "/lab/")
  const rootURL = window.ENV && window.ENV.rootURL || '/';

  // Calcolo della root assoluta
  const absoluteRoot = rootURL.endsWith('/') ? rootURL : rootURL + '/';
  let clean = hash.slice(1).trim(); // "#/scan" → "/scan"

  // Assicura lo slash iniziale
  if (!clean.startsWith('/')) {
    clean = '/' + clean;
  }

  // Normalizza i doppi slash
  clean = clean.replace(/\/{2,}/g, '/');

  // Verifica se la route è valida:
  // - deve iniziare con "/"
  // - deve contenere solo caratteri tipici delle route Ember
  // - non deve contenere "//"
  const pathOnly = clean.split('?')[0]; // rimuove eventuale query

  const isValidRoute = /^\/[a-zA-Z0-9\-_\/]*$/.test(pathOnly);

  // Se route NON valida → redirect alla root del progetto
  if (!isValidRoute) {
    console.warn('Route hash non valida. Redirect a root:', hash);
    window.history.replaceState(null, '', absoluteRoot);
    return;
  }

  // Route valida → costruiamo l'URL finale: rootURL + clean
  const finalPath = absoluteRoot.replace(/\/$/, '') + clean;
  window.history.replaceState(null, '', finalPath + window.location.search);
})();

/**
 * Crea e configura l'Application Ember.
 * Ogni host app chiama: export default createApp(ENV);
 *
 * Under the classic Webpack/Embroider pipeline modules are auto-registered by
 * the AMD loader, so `opts` is empty. Under the Vite/Embroider pipeline there is
 * no AMD loader: the host app's `app/app.js` imports
 * `@embroider/virtual/compat-modules` and passes it here as `opts.compatModules`
 * so `loadInitializers` (and the caller's `Resolver.withModules`) can see them.
 * Keeping the vite-only import in the caller lets this shared factory serve both
 * pipelines without pulling an `@embroider/virtual/*` module into webpack builds.
 *
 * @param {object} ENV - config/environment dell'host app
 * @param {object} [opts]
 * @param {object} [opts.compatModules] - Embroider compat modules (Vite pipeline)
 * @returns {class} App
 */
function createApp(ENV, opts = {}) {
  // Vite/Embroider: `@embroider/virtual/compat-modules` only enumerates files
  // that exist in the app tree. Apps have no `app/config/environment.js` (the
  // runtime config comes from the config-meta-loader shim), so the resolver
  // cannot resolve `config:environment` and every
  // `resolveRegistration('config:environment')` (see beebee-base/lib/get-config)
  // returns undefined. Register the module explicitly with the ENV we already
  // received; app.js passes the same object to `Resolver.withModules`.
  if (opts.compatModules) {
    const configModule = `${ENV.modulePrefix}/config/environment`;
    if (!opts.compatModules[configModule]) {
      opts.compatModules[configModule] = {
        default: ENV
      };
    }
  }

  // ember-basic-dropdown
  setConfig({
    rootElement: ENV.APP.rootElement
  });

  // ----------------------------
  // Inizializza subito Sentry
  // ----------------------------
  if (ENV.sentryDSN && ENV.environment === 'production') {
    const releaseNumber = ENV.APP.version?.split('+')[0];
    ENV.APP.release = [ENV.sentryRelease, releaseNumber].filter(Boolean).join('@');

    // Disabilita instrumentPageLoad per evitare il crash in @sentry/ember
    // quando routerService.recognize(url) ritorna null (es. iframe embedded su Mobile Safari).
    // Il tracing delle navigazioni resta attivo.
    window.__sentryEmberConfig = {
      browserTracingOptions: {
        instrumentPageLoad: false
      }
    };
    Sentry.init({
      dsn: ENV.sentryDSN,
      environment: ENV.environment,
      release: ENV.APP.release,
      tracesSampleRate: 0.05,
      beforeSend(event, hint) {
        const error = hint?.originalException;
        if (error) {
          const msg = typeof error === 'string' ? error : error.message || '';
          // Ember router internal: back/forward (popstate) after the app has
          // navigated away fires finalizeQueryParamChange with an empty
          // routeInfos list, so router._queryParamsFor reads
          // routeInfos[len-1].name on undefined. Not actionable in app code
          // (Sentry EMBER-SCUOLE-ECOMMERCE-28E / -2XN); drop the noise.
          const stack = typeof error === 'object' ? error.stack || '' : '';
          if (stack.includes('_queryParamsFor') || stack.includes('finalizeQueryParamChange')) {
            return null;
          }
          // A fetch the BROWSER cancelled: leaving the page, or a mobile tab put
          // to sleep mid-request, rejects every in-flight request with a
          // DOMException named AbortError (code 20). Nothing in this codebase
          // owns an AbortController, so such a rejection is never ours to act on
          // — it arrives with no stack and buckets as "Non-Error object
          // rejected: {}" (Sentry EMBER-SCUOLE-SEGRETERIA-10H). Matched on the
          // name, since the DOMException carries no usable message.
          if (typeof error === 'object' && error.name === 'AbortError') {
            return null;
          }
          if (msg.includes('TaskCancelation') || msg.includes('The operation is insecure') || msg.includes("Failed to read the 'localStorage'") || msg.includes('Load failed') && msg.includes('.beebeeboard.com') || msg.includes('Load failed') && msg.includes('s3.eu-central-1.amazonaws.com') || msg.includes('Failed to fetch') && msg.includes('.beebeeboard.com') || msg.includes('Failed to fetch') && msg.includes('s3.eu-central-1.amazonaws.com') || msg.includes('Java object is gone') || msg.includes('VM BUG') || msg.includes('Request Rejected with an Unknown Error') || msg.includes('Non-Error promise rejection captured with value')) {
            return null;
          }
          // Object exceptions (e.g. a rejected jQuery-Deferred-shaped promise
          // from a legacy vendor plugin, never a real Error) reach Sentry as
          // "Object captured as exception with keys: ..." with zero stack and
          // zero diagnostic value (audit 2026-07-26, ~330 events across 2
          // apps, no actionable frame). Surface the object's own primitive
          // fields in the message/extra instead of dropping it — turns an
          // opaque bucket into something a future triage can actually read.
          if (typeof error === 'object' && !error.stack) {
            const fields = {};
            for (const k of Object.keys(error)) {
              const v = error[k];
              if (v === null || ['string', 'number', 'boolean'].includes(typeof v)) {
                fields[k] = v;
              }
            }
            const summary = JSON.stringify(fields);
            if (event.exception?.values?.[0]) {
              event.exception.values[0].value = `Non-Error object rejected: ${summary}`;
            }
            event.extra = {
              ...event.extra,
              rejectedObjectFields: fields
            };
          }
        }
        // Lightweight context so an opaque TypeError is at least placeable:
        // viewport, connectivity and — as a fallback before the first route
        // transition sets the `route` tag (see beebee-base/lib/router-tracking)
        // — the raw path at capture time.
        event.tags = {
          ...event.tags,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          online: navigator.onLine
        };
        if (!event.tags.route) {
          event.tags.url_at_capture = window.location.pathname + window.location.hash;
        }
        return event;
      },
      ignoreErrors: [/UnrecognizedURLError/, /TaskCancelation/, /The operation is insecure/, /Node\.removeChild/, /VM BUG/, /infinite rendering invalidation/, /The object can not be found here/, /NetworkError when attempting to fetch/, /The scheduler is not initialized/, /invalid origin/]
    });
  }

  // ----------------------------
  // Messaggio fallback nel loader
  // ----------------------------
  function showBootstrapErrorMessage(message) {
    const loadingApp = document.getElementById('loading-app');
    if (!loadingApp) return;
    loadingApp.innerHTML = `
      <div style="text-align:center; padding:2rem; font-family:sans-serif; max-width:480px; margin:2rem auto;">
        <h2 style="color:#b00020; margin-bottom:1rem;">⚠️ Problema durante l'avvio</h2>
        <p style="margin-bottom:1rem;">${message || 'Si è verificato un errore inatteso.'}</p>
        <p style="font-size:0.9rem; color:#555;">Prova ad aggiornare la pagina o svuota la cache del browser.</p>
        <button style="margin-top:1rem; padding:0.5rem 1rem; cursor:pointer;" onclick="window.location.reload()">Riprova</button>
      </div>
    `;
  }

  // ----------------------------
  // Handler globali temporanei
  // ----------------------------
  // Registered via addEventListener('error', ...), so it is invoked with a
  // single ErrorEvent \u2014 NOT the classic window.onerror 5-arg signature. Read
  // the actual Error off event.error, otherwise the production Sentry capture
  // below never fires for global bootstrap errors (see BUG-REGISTER.md).
  function bootstrapErrorHandler(event) {
    const error = event?.error;
    const message = event?.message;
    console.error('Bootstrap JS error:', message, error);
    if (ENV.environment === 'production' && error) {
      // Tag as bootstrap phase: distinguishes pre-`ready()` failures from
      // steady-state runtime errors, and from @sentry/ember's own global
      // handler catching the same throw.
      Sentry.withScope(scope => {
        scope.setTag('phase', 'bootstrap');
        Sentry.captureException(error);
      });
    }
    showBootstrapErrorMessage('Impossibile avviare l\u2019applicazione.');
  }
  function bootstrapRejectionHandler(event) {
    console.error('Bootstrap unhandled rejection:', event.reason);
    if (ENV.environment === 'production' && event.reason) {
      Sentry.withScope(scope => {
        scope.setTag('phase', 'bootstrap');
        Sentry.captureException(event.reason);
      });
    }
    showBootstrapErrorMessage('Errore durante il caricamento iniziale.');
  }
  window.addEventListener('error', bootstrapErrorHandler);
  window.addEventListener('unhandledrejection', bootstrapRejectionHandler);

  // ----------------------------
  // App Ember Octane
  // ----------------------------
  class App extends Application {
    constructor(...args) {
      super(...args);
      this.modulePrefix = ENV.modulePrefix;
      this.podModulePrefix = ENV.podModulePrefix;
    }
    ready() {
      // Default pre-boot, allineato a FALLBACK_LOCALE (it-IT): evita un flash
      // in inglese prima che la sessione risolva la lingua. Da qui in poi il
      // locale appartiene a session.applyLocale — non scriverlo altrove.
      moment.locale('it');
      const loader = document.getElementById('loading-app');
      if (loader) loader.remove();
      window.removeEventListener('error', bootstrapErrorHandler);
      window.removeEventListener('unhandledrejection', bootstrapRejectionHandler);
    }
  }
  loadInitializers(App, ENV.modulePrefix, opts.compatModules);

  // The global `body.beebee-loading` indicator (formerly driven by jQuery
  // ajaxStart/ajaxStop) is now toggled by an in-flight counter inside
  // beebee-base/lib/utility/ajax, which every request funnels through.

  return App;
}

export { createApp as default };
//# sourceMappingURL=core-app.js.map
